import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import RichTextEditor from '../../components/ui/RichTextEditor';
import { useAuth } from '../../context/AuthContext';
import { formatDateTime } from '../../utils/helpers';
import Button from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import Modal from '../../components/ui/Modal';
import ForwardModal from '../../components/efile/ForwardModal';
import { 
  FileText, Send, CheckCircle, Clock, ArrowRight, User, Printer, 
  Edit2, Save, X, Paperclip, Flag, Link as LinkIcon, Lock, 
  Trash2, Plus, Home, Eye, UploadCloud, History, Archive, Truck,
  Maximize2, Minimize2, ShieldCheck, XCircle, Building2
} from 'lucide-react';
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { motion, AnimatePresence } from 'framer-motion';
import VersionHistoryModal from '../../components/efile/VersionHistoryModal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

const FileWorkspace = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Data States
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState([]);
  const [attachments, setAttachments] = useState([]); // Uploaded documents
  const [flags, setFlags] = useState([]);
  const [linkedFiles, setLinkedFiles] = useState([]);
  const [linkedReceipts, setLinkedReceipts] = useState([]);
  const [linkedDispatches, setLinkedDispatches] = useState([]);
  const [movements, setMovements] = useState([]);
  
  // Versioning State
  const [versionHistory, setVersionHistory] = useState([]);
  const [showVersionHistoryModal, setShowVersionHistoryModal] = useState(false);
  
  // UI States
  const [activeTab, setActiveTab] = useState('noting'); // 'noting', 'flags', 'attachments'
  const [newNoteText, setNewNoteText] = useState('');
  const [subject, setSubject] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isEditingSubject, setIsEditingSubject] = useState(false);
  const [editSubjectValue, setEditSubjectValue] = useState('');
  const [viewerUrl, setViewerUrl] = useState(null); // URL for PDF viewer
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [selectedFlagId, setSelectedFlagId] = useState(null);
  const [selectedAttachmentRefs, setSelectedAttachmentRefs] = useState([]); // [{source,id}]
  const [showGreenSheetPreview, setShowGreenSheetPreview] = useState(false);
  const [greenSheetUrl, setGreenSheetUrl] = useState(null);
  
  // Modal States
  const [showPinModal, setShowPinModal] = useState(false);
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [pin, setPin] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [signedAs, setSignedAs] = useState('');
  const [actionType, setActionType] = useState(''); // 'approve', 'commit' (forward handled by ForwardModal)
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkSearchQuery, setLinkSearchQuery] = useState('');
  const [linkSearchResults, setLinkSearchResults] = useState([]);
  const [deleteDraftId, setDeleteDraftId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showFileDeleteConfirm, setShowFileDeleteConfirm] = useState(false);
  const [isEditorMaximized, setIsEditorMaximized] = useState(false);
  const [isLayoutMaximized, setIsLayoutMaximized] = useState(() => {
    const stored = localStorage.getItem('efile.workspace.sidebarHidden');
    if (stored == null) return true;
    return stored === 'true';
  });
  const [isEditorVisible, setIsEditorVisible] = useState(false);
  const [draftStatus, setDraftStatus] = useState('saved'); // 'saved', 'unsaved', 'saving'
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [forwardLocked, setForwardLocked] = useState(false);
  const [forwardCompleted, setForwardCompleted] = useState(false);

  const notesEndRef = useRef(null);

  const buildGreenSheetUrl = (opts = {}) => {
    const token = localStorage.getItem('token');
    if (!token) {
        toast.error('Authentication required');
        navigate('/login');
        return null;
    }
    const preview = opts.preview ? '&preview=1' : '';
    return `/api/pdf/green-sheet/efile/${id}?token=${encodeURIComponent(token)}${preview}`;
  };

  const handlePrintClick = () => {
    const url = buildGreenSheetUrl();
    if (!url) return;
    window.open(url, '_blank', 'noopener');
  };

  const handlePreviewGreenSheet = () => {
    const url = buildGreenSheetUrl({ preview: true });
    if (!url) return;
    setGreenSheetUrl(url);
    setShowGreenSheetPreview(true);
  };

  useEffect(() => {
    setForwardLocked(false);
    setForwardCompleted(false);
    fetchWorkspace();
  }, [id]);

  useEffect(() => {
    setIsLayoutMaximized(true);
    localStorage.setItem('efile.workspace.sidebarHidden', 'true');
  }, [id]);

  useEffect(() => {
    if (activeTab === 'noting') {
      scrollToBottom();
    }
  }, [notes, activeTab]);

  useEffect(() => {
    if (!file || !user) return;
    const nonDraftCount = Array.isArray(notes) ? notes.filter(n => String(n?.isDraft || 0) !== '1').length : 0;
    const onDesk = String(file.currentDeskId) === String(user.id) || Boolean(file.delegatedFrom);
    const initiatorStage = onDesk && String(file.createdBy) === String(user.id) && nonDraftCount <= 1 && !file.isArchived;
    if (initiatorStage && isEditorVisible) {
      setIsEditorVisible(false);
    }
  }, [file, user, notes, isEditorVisible]);

  const scrollToBottom = () => {
    notesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchWorkspace = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        navigate('/login');
        return;
      }
      const response = await axios.get(`/api/efile/${id}/workspace`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const { file, notes, attachments, flags, links, linkedReceipts, linkedDispatches, movements } = response.data.data;
        setFile(file);
        setNotes(notes);
        setAttachments(attachments || []);
        setFlags(flags || []);
        setLinkedFiles(links || []);
        setLinkedReceipts(linkedReceipts || []);
        setLinkedDispatches(linkedDispatches || []);
        setMovements(movements || []);
        
        // Initialize subject for new note with file subject
        if (!currentDraftId && !subject) {
            setSubject(file.subject);
        }
      }
    } catch (error) {
      console.error('Error fetching workspace:', error);
      const status = error.response?.status;
      const serverMessage = error.response?.data?.message;
      const message = serverMessage || error.message || 'Request failed';
      toast.error(`Failed to load file workspace${status ? ` (${status})` : ''}: ${message}`);
      if (status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewAttachment = async (attachment) => {
    setSelectedAttachment(attachment);
    setActiveTab('attachments');
    
    // Create blob URL for viewing
    try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/files/${attachment.fileName}`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
        });
        const url = URL.createObjectURL(response.data);
        setViewerUrl(url);
    } catch (error) {
        console.error('Error fetching file:', error);
        toast.error('Could not load file preview');
        setViewerUrl(null);
    }
  };

  const handleCancelEdit = () => {
    if (newNoteText.trim() !== '' && draftStatus === 'unsaved') {
      if (!window.confirm("Discard unsaved changes?")) return;
    }
    setNewNoteText('');
    setSubject('');
    setCurrentDraftId(null);
    setDraftStatus('saved');
    setSubmitting(false);
    setIsEditorMaximized(false);
    setIsEditorVisible(false);
  };

  const focusEditor = () => {
    setTimeout(() => {
      try {
        const el = document.querySelector('#official-note-editor .ProseMirror');
        if (el && el.isConnected) el.focus();
      } catch (e) {
        void e;
      }
    }, 150);

    setTimeout(() => {
      const editorEl = document.getElementById('official-note-editor');
      if (editorEl) {
        editorEl.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const toggleEditorSection = () => {
    if (isEditorVisible) {
      setIsEditorVisible(false);
      setIsEditorMaximized(false);
      return;
    }
    if (!subject) setSubject(file?.subject || '');
    setIsEditorVisible(true);
    focusEditor();
  };

  const startNewNoting = () => {
    if (newNoteText.trim() !== '' && draftStatus === 'unsaved') {
      if (!window.confirm("Discard unsaved changes?")) return;
    }
    setIsEditorVisible(true);
    setIsEditorMaximized(false);
    setNewNoteText('');
    setCurrentDraftId(null);
    setDraftStatus('saved');
    setLastSavedAt(null);
    setWordCount(0);
    setSubject(file?.subject || '');
    focusEditor();
  };

  const handleSaveDraft = async () => {
    // Strip HTML tags to check if empty
    const plainText = newNoteText.replace(/<[^>]+>/g, '').trim();
    if (!plainText) {
      toast.error('Please enter a note');
      return;
    }

    setSubmitting(true);
    setDraftStatus('saving');
    try {
      const token = localStorage.getItem('token');
      const postDraft = (draftIdValue) => axios.post(
          `/api/efile/${id}/draft`,
          {
            remark: newNoteText,
            subject: subject,
            draftId: draftIdValue
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

      let response;
      try {
        response = await postDraft(currentDraftId);
      } catch (err) {
        const status = err?.response?.status;
        const message = err?.response?.data?.message;
        const shouldRetry = Boolean(currentDraftId) && (status === 404 || message === 'Draft not found');
        if (!shouldRetry) throw err;

        setCurrentDraftId(null);
        response = await postDraft(null);
      }

      if (response.data.success) {
        toast.success('Draft saved successfully');
        // Don't clear text, keep it for editing
        // setNewNoteText(''); 
        // setSubject('');
        setDraftStatus('saved');
        setLastSavedAt(new Date());
        
        if (response.data.data && response.data.data.draftId) {
            setCurrentDraftId(response.data.data.draftId);
        }
        
        fetchWorkspace();
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error(error.response?.data?.message || 'Failed to save draft');
      setDraftStatus('unsaved');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNoteChange = (content) => {
    setNewNoteText(content);
    setDraftStatus('unsaved');
    
    // Calculate word count
    const text = content.replace(/<[^>]*>/g, '').trim();
    setWordCount(text ? text.split(/\s+/).length : 0);
  };

  const handleUpdateSubject = async () => {
    if (!editSubjectValue.trim()) {
      toast.error('Subject cannot be empty');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `/api/efile/${id}/subject`,
        { subject: editSubjectValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Subject updated successfully');
        setFile(prev => ({ ...prev, subject: editSubjectValue }));
        setIsEditingSubject(false);
      }
    } catch (error) {
      console.error('Error updating subject:', error);
      toast.error(error.response?.data?.message || 'Failed to update subject');
    } finally {
      setSubmitting(false);
    }
  };



  const initiateAction = (type, draftId = null) => {
    const statusLower = String(file?.status || '').trim().toLowerCase();
    const isCreator = String(file?.createdBy) === String(user?.id);
    const nonDraftCount = Array.isArray(notes) ? notes.filter(n => String(n?.isDraft || 0) !== '1').length : 0;
    const movementCountLocal = Array.isArray(movements) ? movements.length : 0;
    const initiatorAlreadyForwarded = isCreator && nonDraftCount <= 1 && (statusLower !== 'created' || movementCountLocal > 1);

    if (type === 'forward' && (forwardCompleted || initiatorAlreadyForwarded)) {
      toast.error('You have already forwarded this file as Initiator');
      return;
    }

    if (type === 'forward' || type === 'commit') {
        const plainText = newNoteText.replace(/<[^>]+>/g, '').trim();
        if (!plainText && !draftId && type !== 'forward') {
            toast.error('Please enter a note');
            return;
        }
        if (type === 'forward') {
            setShowForwardModal(true);
            return;
        }
    }
    
    setActionType(type);
    if (type === 'approve') setSignedAs('Approver');
    if (type === 'commit') setSignedAs('Forwarder');
    if (type === 'resume_edit') setSignedAs('');
    if (draftId) setCurrentDraftId(draftId);
    setShowPinModal(true);
  };

  const handleForwardConfirm = async (toUserId, eSignPin, mfaCode, signedAs) => {
      if (forwardLocked || forwardCompleted) return;
      setForwardLocked(true);
      try {
        const token = localStorage.getItem('token');
        const plainText = String(newNoteText || '').replace(/<[^>]+>/g, '').trim();

        const response = (isInitiatorStage && !plainText) || (!isInitiatorStage && !plainText && !currentDraftId)
          ? await axios.post(`/api/efile/${id}/forward`, {
              toUserId: toUserId,
              eSignPin: eSignPin,
              mfaCode: mfaCode,
              signedAs: signedAs
            }, {
              headers: { Authorization: `Bearer ${token}` }
            })
          : await axios.post(`/api/efile/${id}/note-and-forward`, {
              remark: newNoteText,
              subject: subject,
              toUserId: toUserId,
              eSignPin: eSignPin,
              mfaCode: mfaCode,
              signedAs: signedAs,
              draftId: currentDraftId // Pass draftId to update draft status
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });

        if (response.data.success) {
            toast.success('File forwarded successfully');
            setForwardCompleted(true);
            setNewNoteText('');
            setSubject('');
            setCurrentDraftId(null);
            fetchWorkspace(); // Or navigate away? Usually stay or go to desk.
            navigate('/dashboard'); // Go to dashboard as file is no longer on desk
        }
      } catch (error) {
          toast.error(error.response?.data?.message || 'Forwarding failed');
          setForwardLocked(false);
          throw error;
      }
  };

  const loadDraft = (draftId) => {
    console.log("Loading draft:", draftId);
    const draft = notes.find(n => String(n.id) === String(draftId));
    
    if (draft) {
        // Ensure remark is not null/undefined
        const content = draft.remark || '';
        
        // Update state
        setIsEditorVisible(true);
        setIsEditorMaximized(false);
        setNewNoteText(content);
        setSubject(draft.subject || file?.subject || '');
        setCurrentDraftId(draftId);
        setDraftStatus('saved');

        toast.success("Draft loaded into editor");
        focusEditor();
    } else {
        toast.error(`Draft not found (ID: ${draftId})`);
    }
  };

  const copyNoteToEditor = (noteId) => {
    const note = notes.find(n => String(n.id) === String(noteId));
    if (!note) {
      toast.error(`Note not found (ID: ${noteId})`);
      return;
    }

    setIsEditorVisible(true);
    setIsEditorMaximized(false);
    setNewNoteText(note.remark || '');
    setSubject(note.subject || file?.subject || '');
    setCurrentDraftId(null);
    setDraftStatus('unsaved');
    focusEditor();
  };

  // Removed useEffect watcher to avoid conflicts with loadDraft's direct manipulation

  const handlePinSubmit = async () => {
    if (!pin || pin.length !== 4) {
        toast.error('Please enter a valid 4-digit PIN');
        return;
    }

    if (actionType === 'resume_edit') {
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/efile/verify-pin', { pin }, { headers: { Authorization: `Bearer ${token}` } });
            
            setShowPinModal(false);
            setPin('');
            
            loadDraft(currentDraftId);
        } catch (error) {
            console.error('Error verifying PIN:', error);
            toast.error(error.response?.data?.message || 'Invalid PIN');
        }
        return;
    }

    if (!signedAs) {
        toast.error('Please select sign role');
        return;
    }
    if (!mfaCode || String(mfaCode).trim().length < 6) {
        toast.error('Please enter your 6-digit MFA code');
        return;
    }

    setShowPinModal(false);
    setSubmitting(true);

    try {
        const token = localStorage.getItem('token');
        let endpoint = '';
        let payload = {};

        if (actionType === 'approve') {
            endpoint = `/api/efile/${id}/approve-close`;
            payload = {
                remark: newNoteText,
                subject: subject,
                eSignPin: pin,
                mfaCode: mfaCode,
                signedAs: signedAs,
                draftId: currentDraftId // Pass draftId to update draft status
            };
        } else if (actionType === 'commit') {
            endpoint = `/api/efile/${id}/commit-draft`;
            payload = {
                draftId: currentDraftId,
                remark: newNoteText || 'Draft Committed',
                subject: subject,
                toUserId: null, // Commit doesn't forward
                eSignPin: pin,
                mfaCode: mfaCode,
                signedAs: signedAs
            };
        }

        const response = await axios.post(endpoint, payload, {
            headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
            toast.success('Action completed successfully');
            setNewNoteText('');
            setSubject('');
            setPin('');
            setMfaCode('');
            setCurrentDraftId(null);
            
            if (actionType === 'approve') {
                navigate('/dashboard');
            } else {
                fetchWorkspace();
            }
        }
    } catch (error) {
        console.error('Error executing action:', error);
        toast.error(error.response?.data?.message || 'Action failed');
        setShowPinModal(true); 
    } finally {
        setSubmitting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
    }

    setSubmitting(true);
    try {
        const token = localStorage.getItem('token');
        const response = await axios.post(`/api/efile/${id}/upload`, formData, {
            headers: { 
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        });

        if (response.data.success) {
            toast.success('Files uploaded successfully');
            fetchWorkspace();
        }
    } catch (error) {
        console.error('Error uploading files:', error);
        toast.error('Failed to upload files');
    } finally {
        setSubmitting(false);
    }
  };

  const handleGenerateDispatch = async () => {
    try {
        setLoading(true); 
        const token = localStorage.getItem('token');
        const response = await axios.post(`/api/dispatch/generate-from-efile/${id}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success) {
            toast.success('Dispatch draft created');
            navigate(`/dispatch/${response.data.data.id}`);
        }
    } catch (error) {
        console.error('Error creating dispatch:', error);
        toast.error('Failed to create dispatch');
    } finally {
        setLoading(false);
    }
  };

  const handleArchive = async () => {
      setShowArchiveConfirm(true);
  };
  
  const confirmArchive = async () => {
      try {
          setLoading(true);
          const token = localStorage.getItem('token');
          const response = await axios.put(`/api/efile/${id}/archive`, {}, {
              headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.success) {
              toast.success('File archived successfully');
              navigate('/record-room');
          }
      } catch (error) {
          console.error('Error archiving file:', error);
          toast.error('Failed to archive file');
      } finally {
          setLoading(false);
          setShowArchiveConfirm(false);
      }
  };

  const handleDeleteDraft = (draftId) => {
      setDeleteDraftId(draftId);
      setShowDeleteConfirm(true);
  };

  const confirmDeleteDraft = async () => {
      if (!deleteDraftId) return;
      
      setSubmitting(true);
      try {
          const token = localStorage.getItem('token');
          await axios.delete(`/api/efile/${id}/draft/${deleteDraftId}`, {
              headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Draft deleted successfully');
          fetchWorkspace();
      } catch (error) {
          console.error('Error deleting draft:', error);
          toast.error('Failed to delete draft');
      } finally {
          setSubmitting(false);
          setShowDeleteConfirm(false);
          setDeleteDraftId(null);
      }
  };

  const confirmDeleteFile = async () => {
      setSubmitting(true);
      try {
          const token = localStorage.getItem('token');
          await axios.delete(`/api/efile/${id}`, {
              headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('File deleted successfully');
          navigate('/dashboard');
      } catch (error) {
          console.error('Error deleting file:', error);
          toast.error(error.response?.data?.message || 'Failed to delete file');
      } finally {
          setSubmitting(false);
          setShowFileDeleteConfirm(false);
      }
  };

  const handleSearchForLink = async () => {
      if (!linkSearchQuery) return;
      try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`/api/efile/active?search=${linkSearchQuery}`, {
              headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.success) {
              setLinkSearchResults(response.data.data.files.filter(f => f.id !== parseInt(id)));
          }
      } catch (error) {
          console.error('Search error:', error);
      }
  };

  const handleLinkFile = async (targetFileId) => {
      try {
          const token = localStorage.getItem('token');
          await axios.post(`/api/efile/${id}/links`, { linkedFileId: targetFileId }, {
              headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('File linked');
          setShowLinkModal(false);
          setLinkSearchQuery('');
          setLinkSearchResults([]);
          fetchWorkspace();
      } catch (error) {
          toast.error('Failed to link file');
      }
  };

  const [showFlagModal, setShowFlagModal] = useState(false);
  const [flagLabel, setFlagLabel] = useState('');
  const [showCreateFlagModal, setShowCreateFlagModal] = useState(false);
  const [showAttachToFlagModal, setShowAttachToFlagModal] = useState(false);
  const [attachTargetFlagId, setAttachTargetFlagId] = useState('');

  const handleAddFlag = () => {
    if ((!selectedAttachmentRefs || selectedAttachmentRefs.length === 0) && !selectedAttachment) {
      toast.error('Select an attachment first');
      return;
    }
    setFlagLabel('');
    setShowFlagModal(true);
  };

  const openCreateFlag = () => {
    setFlagLabel('');
    setShowCreateFlagModal(true);
  };

  const confirmCreateFlag = async () => {
    if (!flagLabel.trim()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/api/efile/${id}/flags`, { flagLabel: flagLabel.trim(), color: 'red' }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Flag created');
      setShowCreateFlagModal(false);
      setFlagLabel('');
      fetchWorkspace();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to create flag');
    }
  };

  const openAttachToFlag = (flagId = '') => {
    if (!selectedAttachmentRefs || selectedAttachmentRefs.length === 0) {
      toast.error('Select one or more attachments first');
      return;
    }
    setAttachTargetFlagId(flagId ? String(flagId) : '');
    setShowAttachToFlagModal(true);
  };

  const confirmAttachToFlag = async () => {
    if (!attachTargetFlagId) {
      toast.error('Select a flag');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/efile/${id}/flags/${attachTargetFlagId}/items`,
        { attachments: selectedAttachmentRefs.map(r => ({ source: r.source, id: r.id, pageNumber: 1 })) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Attached to flag');
      setShowAttachToFlagModal(false);
      setAttachTargetFlagId('');
      fetchWorkspace();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to attach to flag');
    }
  };

  const removeTagFromAttachment = async (flagId, itemId) => {
    if (!flagId || !itemId) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/efile/${id}/flags/${flagId}/items/${itemId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Tag removed');
      fetchWorkspace();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to remove tag');
    }
  };

  const deleteAttachment = async (att) => {
    if (!att || att.source !== 'e_file_document') {
      toast.error('Only uploaded attachments can be removed');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/efile/doc/${att.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Attachment removed');
      if (selectedAttachment && selectedAttachment.source === att.source && String(selectedAttachment.id) === String(att.id)) {
        setSelectedAttachment(null);
        setViewerUrl(null);
      }
      setSelectedAttachmentRefs(prev => prev.filter(r => !(r.source === att.source && String(r.id) === String(att.id))));
      fetchWorkspace();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to remove attachment');
    }
  };

  const confirmAddFlag = async () => {
      if (!flagLabel.trim()) return;

      try {
          const token = localStorage.getItem('token');
          const attachmentsPayload =
            (selectedAttachmentRefs && selectedAttachmentRefs.length > 0)
              ? selectedAttachmentRefs.map(r => ({ source: r.source, id: r.id, pageNumber: 1 }))
              : (selectedAttachment ? [{ source: selectedAttachment.source || 'e_file_document', id: selectedAttachment.id, pageNumber: 1 }] : []);

          if (attachmentsPayload.length === 0) {
            toast.error('Select at least one attachment');
            return;
          }

          await axios.post(`/api/efile/${id}/flags`, {
              attachments: attachmentsPayload,
              flagLabel: flagLabel,
              color: 'red'
          }, {
              headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Flag added');
          fetchWorkspace();
          setShowFlagModal(false);
          setSelectedAttachmentRefs([]);
      } catch (error) {
          toast.error(error?.response?.data?.message || 'Failed to add flag');
      }
  };

  const handleUploadVersionClick = (att) => {
    setSelectedAttachment(att);
    document.getElementById('file-upload-version').click();
  };

  const handleVersionFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedAttachment) return;
    
    const formData = new FormData();
    formData.append('files', file);

    try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await axios.post(`/api/upload/version/${selectedAttachment.id}`, formData, {
             headers: { 
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
             }
        });

        if (response.data.success) {
            toast.success('New version uploaded');
            const newVersion = response.data.data;

            // Immediately view the new version
            try {
                let fetchUrl = newVersion.fileUrl;
                if (fetchUrl && !fetchUrl.startsWith('/api')) {
                    fetchUrl = `/api${fetchUrl}`;
                }

                const blobResponse = await axios.get(fetchUrl, {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                });
                const blobUrl = URL.createObjectURL(blobResponse.data);
                setViewerUrl(blobUrl);
                
                // Update selectedAttachment so it highlights the new version in the list
                setSelectedAttachment({
                    ...selectedAttachment, // keep other props
                    id: newVersion.id,
                    fileName: newVersion.fileName,
                    originalName: newVersion.originalName,
                    versionNumber: newVersion.versionNumber,
                    // If backend returns more fields in newVersion, they will overwrite
                });
            } catch (err) {
                console.error('Error viewing new version:', err);
                toast.error('Version uploaded but failed to open viewer');
            }

            fetchWorkspace();
        }
    } catch (error) {
        console.error('Error uploading version:', error);
        toast.error('Failed to upload version');
    } finally {
        setLoading(false);
        e.target.value = null; 
    }
  };

  const handleViewHistory = async (att) => {
      try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`/api/upload/history/${att.id}`, {
              headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.success) {
              setVersionHistory(response.data.data);
              setSelectedAttachment(att);
              setShowVersionHistoryModal(true);
          }
      } catch (error) {
          toast.error('Failed to load history');
      }
  };

  const handleViewVersion = (ver) => {
      let url = '';
      const token = localStorage.getItem('token');
      // Infer URL based on structure (e_file_documents has fileId, file_attachments has entityType)
      if (ver.fileId) {
         url = `/api/efile/doc/${ver.id}?token=${token}`;
      } else {
         url = `/api/files/${ver.fileName}?token=${token}`;
      }
      setViewerUrl(url);
      setShowVersionHistoryModal(false);
  };

  if (loading) return <div className="flex justify-center items-center h-full"><LoadingSpinner /></div>;
  if (!file) return <div className="text-center p-8 text-gray-500">File not found or access denied.</div>;

  const canApprove = ['Head', 'GM', 'SuperAdmin'].includes(user?.role);
  // Check if file is on desk (either directly or via delegation)
  const isOnMyDesk = file && user && (
    String(file.currentDeskId) === String(user.id) || 
    Boolean(file.delegatedFrom)
  );
  const isEditable = isOnMyDesk && !file.isArchived;
  const nonDraftNoteCount = Array.isArray(notes) ? notes.filter(n => String(n?.isDraft || 0) !== '1').length : 0;
  const movementCount = Array.isArray(movements) ? movements.length : 0;
  const statusLower = String(file.status || '').trim().toLowerCase();
  const isCreator = String(file.createdBy) === String(user?.id);
  const initiatorAlreadyForwarded = isCreator && nonDraftNoteCount <= 1 && (statusLower !== 'created' || movementCount > 1);
  const isInitiatorStage =
    isEditable &&
    String(file.createdBy) === String(user?.id) &&
    nonDraftNoteCount <= 1 &&
    movementCount <= 1 &&
    statusLower === 'created' &&
    !forwardCompleted;
  const canManageAttachments = isInitiatorStage;
  const canAttachInRemarks = isEditable && !isInitiatorStage;

  return (
    <div className="h-full bg-gray-50 overflow-hidden relative">
      {file.delegatedFrom && (
        <div className="bg-amber-100 border-b border-amber-200 px-4 py-2 text-sm text-amber-800 flex items-center justify-center font-medium">
            <User className="h-4 w-4 mr-2" />
            You are accessing this file on behalf of {file.delegatedFrom}
        </div>
      )}
      {file.isArchived === 1 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[100] opacity-10">
            <div className="transform -rotate-45 text-9xl font-black text-red-900 border-8 border-red-900 px-20 py-10 rounded-3xl tracking-widest uppercase">
                ARCHIVED
            </div>
        </div>
      )}
      
      <PanelGroup direction="horizontal">
      {/* LEFT PANE: Flags & Attachments (40%) */}
      {!isLayoutMaximized && (
      <>
      <Panel defaultSize={40} minSize={20} className="flex flex-col border-r border-gray-200 bg-white">
        {/* Tabs Header */}
        <div className="flex border-b border-gray-200">
            <button 
                onClick={() => setActiveTab('attachments')}
                className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 ${activeTab === 'attachments' ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50' : 'text-gray-500 hover:text-gray-700'}`}
            >
                <Paperclip className="h-4 w-4" /> Attachments ({attachments.length})
            </button>
            <button 
                onClick={() => setActiveTab('flags')}
                className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 ${activeTab === 'flags' ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50' : 'text-gray-500 hover:text-gray-700'}`}
            >
                <Flag className="h-4 w-4" /> Flags ({flags.length})
            </button>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-4">
            {activeTab === 'attachments' && (
                <div className="space-y-4">
                    {/* Upload Area */}
                    {canManageAttachments && (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:bg-gray-50 transition-colors">
                            <input 
                                type="file" 
                                multiple 
                                onChange={handleFileUpload}
                                className="hidden" 
                                id="file-upload"
                            />
                            <input 
                                type="file" 
                                onChange={handleVersionFileChange}
                                className="hidden" 
                                id="file-upload-version"
                            />
                            <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center gap-1">
                                <Plus className="h-6 w-6 text-gray-400" />
                                <span className="text-sm text-gray-600 font-medium">Upload New Attachment</span>
                            </label>
                        </div>
                    )}

                    {/* Attachment List */}
                    <div className="space-y-2">
                        {(() => {
                          const selectedFlag = selectedFlagId ? flags.find(f => String(f.id) === String(selectedFlagId)) : null;
                          const filteredAttachments = selectedFlag
                            ? attachments.filter(att => Array.isArray(selectedFlag.attachmentRefs) && selectedFlag.attachmentRefs.some(r => r?.source === att.source && String(r?.id) === String(att.id)))
                            : attachments;

                          return (
                            <>
                              {selectedFlag && (
                                <div className="flex items-center justify-between p-2 rounded border border-indigo-200 bg-indigo-50 text-xs text-indigo-700">
                                  <div className="truncate">
                                    Showing attachments for flag <span className="font-bold">{selectedFlag.flagLabel}</span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setSelectedFlagId(null)}
                                    className="px-2 py-1 rounded bg-white border border-indigo-200 hover:bg-indigo-100"
                                  >
                                    Clear
                                  </button>
                                </div>
                              )}

                              {filteredAttachments.map(att => (
                            <div 
                                key={`${att.source}:${att.id}`} 
                                onClick={() => handleViewAttachment(att)}
                                className={`p-3 rounded border cursor-pointer flex items-center justify-between group ${(selectedAttachment && selectedAttachment.id === att.id && selectedAttachment.source === att.source) ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-gray-200 hover:border-indigo-300'}`}
                            >
                                <div className="flex items-center gap-3 overflow-hidden">
                                    {canManageAttachments && (
                                      <input
                                        type="checkbox"
                                        checked={selectedAttachmentRefs.some(r => r.source === att.source && String(r.id) === String(att.id))}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          setSelectedAttachmentRefs(prev => {
                                            const exists = prev.some(r => r.source === att.source && String(r.id) === String(att.id));
                                            if (exists) return prev.filter(r => !(r.source === att.source && String(r.id) === String(att.id)));
                                            return [...prev, { source: att.source, id: att.id }];
                                          });
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="h-4 w-4 accent-indigo-600"
                                        title="Select for batch flag"
                                      />
                                    )}
                                    <div className="bg-gray-100 p-2 rounded text-gray-500">
                                        <FileText className="h-5 w-5" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-gray-900 truncate">
                                            {att.originalName}
                                            <span className="ml-2 px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 rounded border border-gray-200">v{att.versionNumber || 1}</span>
                                        </div>
                                        <div className="text-xs text-gray-500">{formatDateTime(att.createdAt)} • {(att.fileSize / 1024).toFixed(1)} KB</div>
                                        {Array.isArray(att.flagRefs) && att.flagRefs.length > 0 && (
                                          <div className="mt-1 flex flex-wrap gap-1">
                                            {att.flagRefs.map((ref) => (
                                              <span
                                                key={`${ref.flagId}:${ref.itemId || 'x'}`}
                                                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold bg-red-50 text-red-700 rounded border border-red-200"
                                              >
                                                <span>{ref.flagLabel}</span>
                                                {canManageAttachments && ref.itemId && (
                                                  <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); removeTagFromAttachment(ref.flagId, ref.itemId); }}
                                                    className="ml-0.5 text-red-700 hover:text-red-900"
                                                    title="Remove tag"
                                                  >
                                                    ×
                                                  </button>
                                                )}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {canManageAttachments && (
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); handleUploadVersionClick(att); }}
                                            className="p-1.5 hover:bg-indigo-50 text-indigo-600 rounded"
                                            title="Upload New Version"
                                        >
                                            <UploadCloud className="h-4 w-4" />
                                        </button>
                                    )}
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); handleViewHistory(att); }}
                                        className="p-1.5 hover:bg-gray-100 text-gray-600 rounded"
                                        title="History"
                                    >
                                        <History className="h-4 w-4" />
                                    </button>
                                    {canManageAttachments && (
                                        <button 
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedAttachment(att);
                                              setSelectedAttachmentRefs([{ source: att.source, id: att.id }]);
                                              openAttachToFlag();
                                            }}
                                            className="p-1.5 hover:bg-red-50 text-red-500 rounded" 
                                            title="Attach to Flag"
                                        >
                                            <Flag className="h-4 w-4" />
                                        </button>
                                    )}
                                    {canManageAttachments && att.source === 'e_file_document' && (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); deleteAttachment(att); }}
                                        className="p-1.5 hover:bg-red-50 text-red-600 rounded"
                                        title="Remove Attachment"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    )}
                                </div>
                            </div>
                              ))}

                              {filteredAttachments.length === 0 && (
                                <div className="text-center text-gray-400 py-8 text-sm">No attachments found</div>
                              )}
                            </>
                          );
                        })()}
                        {attachments.length > 0 && canManageAttachments && selectedAttachmentRefs.length > 0 && (
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            <button
                              type="button"
                              onClick={openAttachToFlag}
                              className="px-3 py-2 rounded border border-red-200 bg-red-50 text-red-700 text-sm font-medium hover:bg-red-100"
                            >
                              Attach to Flag ({selectedAttachmentRefs.length})
                            </button>
                            <button
                              type="button"
                              onClick={() => { setSelectedAttachmentRefs([]); }}
                              className="px-3 py-2 rounded border border-gray-200 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
                            >
                              Clear Selection
                            </button>
                          </div>
                        )}
                        {attachments.length === 0 && (
                            <div className="text-center text-gray-400 py-8 text-sm">No attachments found</div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'flags' && (
                <div className="space-y-3">
                    {canManageAttachments && (
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={openCreateFlag}
                          className="px-3 py-2 rounded border border-gray-200 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50"
                        >
                          Create Flag
                        </button>
                        <button
                          type="button"
                          onClick={() => openAttachToFlag()}
                          disabled={!selectedAttachmentRefs || selectedAttachmentRefs.length === 0}
                          className="px-3 py-2 rounded border border-red-200 bg-red-50 text-red-700 text-sm font-medium hover:bg-red-100 disabled:opacity-50"
                        >
                          Attach Selected
                        </button>
                      </div>
                    )}
                    {flags.map(flag => {
                        const count = Array.isArray(flag.attachmentRefs) ? flag.attachmentRefs.length : (flag.attachmentId ? 1 : 0);
                        const active = String(selectedFlagId) === String(flag.id);
                        return (
                          <button
                            type="button"
                            key={flag.id}
                            onClick={() => setSelectedFlagId(active ? null : flag.id)}
                            className={`w-full text-left flex items-center gap-3 p-3 rounded-lg border ${active ? 'bg-indigo-50 border-indigo-200' : 'bg-red-50 border-red-100 hover:border-red-200'}`}
                          >
                            <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold text-xs shrink-0">
                              {flag.flagLabel}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">Flag {flag.flagLabel}</div>
                              <div className="text-xs text-gray-500">{count} attachment{count === 1 ? '' : 's'}</div>
                            </div>
                          </button>
                        );
                    })}
                    {flags.length === 0 && (
                        <div className="text-center text-gray-400 py-8 text-sm">No flags added yet</div>
                    )}

                    {selectedFlagId && (() => {
                      const selectedFlag = flags.find(f => String(f.id) === String(selectedFlagId));
                      if (!selectedFlag) return null;
                      const related = attachments.filter(att => Array.isArray(selectedFlag.attachmentRefs) && selectedFlag.attachmentRefs.some(r => r?.source === att.source && String(r?.id) === String(att.id)));
                      return (
                        <div className="mt-4 p-3 rounded border border-gray-200 bg-white">
                          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                            Attachments in {selectedFlag.flagLabel}
                          </div>
                          <div className="space-y-2">
                            {related.map(att => {
                              const ref = Array.isArray(selectedFlag.attachmentRefs)
                                ? selectedFlag.attachmentRefs.find(r => r?.source === att.source && String(r?.id) === String(att.id))
                                : null;
                              return (
                                <div
                                  key={`${att.source}:${att.id}`}
                                  className="w-full text-left p-2 rounded border border-gray-200 hover:border-indigo-300 flex items-start justify-between gap-2"
                                >
                                  <button
                                    type="button"
                                    onClick={() => { setActiveTab('attachments'); handleViewAttachment(att); }}
                                    className="min-w-0 text-left"
                                  >
                                    <div className="text-sm font-medium text-gray-900 truncate">{att.originalName}</div>
                                    <div className="text-xs text-gray-500">{formatDateTime(att.createdAt)} • {(att.fileSize / 1024).toFixed(1)} KB</div>
                                  </button>
                                  {canManageAttachments && ref?.itemId && (
                                    <button
                                      type="button"
                                      onClick={() => removeTagFromAttachment(selectedFlag.id, ref.itemId)}
                                      className="shrink-0 px-2 py-1 rounded border border-red-200 bg-red-50 text-red-700 text-xs font-bold hover:bg-red-100"
                                      title="Remove this attachment from the flag"
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                            {related.length === 0 && (
                              <div className="text-center text-gray-400 py-4 text-sm">No attachments linked to this flag</div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                </div>
            )}
        </div>

        {/* PDF Viewer Placeholder */}
        <div className="h-1/2 border-t border-gray-200 bg-gray-100 flex flex-col">
            <div className="px-4 py-2 bg-white border-b border-gray-200 text-xs font-bold text-gray-500 uppercase tracking-wide flex justify-between items-center">
                <span>Document Preview</span>
                {selectedAttachment && <span className="text-indigo-600 truncate max-w-[200px]">{selectedAttachment.originalName}</span>}
            </div>
            <div className="flex-1 relative overflow-hidden">
                {viewerUrl ? (
                    <iframe src={viewerUrl} className="w-full h-full border-0" title="Document Viewer" />
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <Eye className="h-8 w-8 opacity-50" />
                        <span className="text-sm">Select an attachment to preview</span>
                    </div>
                )}
            </div>
        </div>
      </Panel>

      <PanelResizeHandle className="w-1 bg-gray-200 hover:bg-indigo-400 transition-colors cursor-col-resize" />
      </>
      )}

      {/* RIGHT PANE: Note Sheet & Actions (60%) */}
      <Panel defaultSize={60} minSize={30} className="flex flex-col bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm shrink-0">
            <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                    <Link to="/dashboard" className="text-gray-400 hover:text-indigo-600 transition-colors">
                        <Home className="h-5 w-5" />
                    </Link>
                    <span className="text-gray-300">/</span>
                    <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-sm font-medium text-gray-700">{file.fileNumber}</span>
                </div>
                <div className="flex items-center gap-2">
                                <Button 
                                    onClick={() => {
                                      const next = !isLayoutMaximized;
                                      setIsLayoutMaximized(next);
                                      localStorage.setItem('efile.workspace.sidebarHidden', String(next));
                                    }}
                                    variant="outline"
                                    size="sm"
                                    className="text-xs h-8 gap-1.5"
                                    title={isLayoutMaximized ? "Show Attachments Panel" : "Hide Attachments Panel"}
                                >
                                    {isLayoutMaximized ? <Maximize2 className="h-3.5 w-3.5" /> : <Minimize2 className="h-3.5 w-3.5" />}
                                    {isLayoutMaximized ? 'Show Sidebar' : 'Hide Sidebar'}
                                </Button>
                                <Button 
                        onClick={handlePreviewGreenSheet}
                        variant="outline"
                        size="sm"
                        className="text-xs h-8 gap-1.5"
                    >
                        <Eye className="h-3.5 w-3.5" /> Preview
                    </Button>
                                <Button 
                        onClick={handlePrintClick}
                        variant="outline"
                        size="sm"
                        className="text-xs h-8 gap-1.5"
                    >
                        <Printer className="h-3.5 w-3.5" /> Print
                    </Button>
                    {canApprove && !file.isArchived && (
                        <Button 
                            onClick={handleArchive}
                            variant="outline"
                            size="sm"
                            className="text-xs h-8 gap-1.5 text-red-600 border-red-200 hover:bg-red-50"
                        >
                            <Archive className="h-3.5 w-3.5" /> Archive
                        </Button>
                    )}
                    {/* Delete Button for Creator (Only if status is Created/Draft) */}
                    {isOnMyDesk && file.createdBy === user?.id && !file.isArchived && file.status === 'Created' && (
                        <Button 
                            onClick={() => setShowFileDeleteConfirm(true)}
                            variant="outline"
                            size="sm"
                            className="text-xs h-8 gap-1.5 text-red-600 border-red-200 hover:bg-red-50"
                        >
                            <Trash2 className="h-3.5 w-3.5" /> Delete
                        </Button>
                    )}
                    {(file.status === 'Closed' || file.status === 'Approved') && (
                        <Button 
                            onClick={handleGenerateDispatch}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white text-xs h-8 gap-1.5 ml-2"
                        >
                            <Truck className="h-3.5 w-3.5" /> Generate Official Dispatch
                        </Button>
                    )}
                </div>
            </div>

            <h1 className="text-lg font-bold text-gray-900 leading-tight mb-2 flex items-center gap-2">
                {isEditingSubject ? (
                    <div className="flex items-center gap-2 flex-1">
                        <Input value={editSubjectValue} onChange={e => setEditSubjectValue(e.target.value)} className="h-8 text-sm" />
                        <Button size="sm" onClick={handleUpdateSubject}><Save className="h-4 w-4" /></Button>
                        <Button size="sm" variant="outline" onClick={() => setIsEditingSubject(false)}><X className="h-4 w-4" /></Button>
                    </div>
                ) : (
                    <>
                        {file.subject}
                        {isOnMyDesk && <button onClick={() => { setEditSubjectValue(file.subject); setIsEditingSubject(true); }} className="text-gray-400 hover:text-indigo-600"><Edit2 className="h-3.5 w-3.5" /></button>}
                    </>
                )}
            </h1>

            {/* Hierarchy Context */}
            {(file.wingName || file.departmentName || file.sectionName) && (
                <div className="flex items-center gap-2 mt-1 mb-2 text-xs font-medium text-gray-500 bg-gray-50 px-2 py-1 rounded w-fit">
                    <Building2 className="h-3.5 w-3.5 text-gray-400" />
                    <span>
                        {[file.wingName, file.departmentName, file.sectionName].filter(Boolean).join(' › ')}
                    </span>
                </div>
            )}

            {/* Linked Files Section */}
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                    <LinkIcon className="h-3 w-3" /> Linked Files:
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {linkedFiles.map(link => (
                        <Link key={link.id} to={`/efile/${link.linkedFileId}`} target="_blank" className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs hover:bg-blue-100 transition-colors border border-blue-200 flex items-center gap-1">
                            <FileText className="h-3 w-3" /> {link.fileNumber}
                        </Link>
                    ))}
                    {isOnMyDesk && (
                        <button onClick={() => setShowLinkModal(true)} className="text-indigo-600 hover:text-indigo-800 text-xs font-medium px-2 py-1 rounded hover:bg-indigo-50 transition-colors flex items-center gap-1">
                            <Plus className="h-3 w-3" /> Link File
                        </button>
                    )}
                </div>
            </div>

            <div className="flex items-center gap-4 mt-2 pt-2 border-t border-gray-100">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                    <LinkIcon className="h-3 w-3" /> Linked Dak:
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {linkedReceipts.map(r => (
                        <Link key={`r-${r.id}`} to={`/diary/${r.id}`} target="_blank" className="bg-emerald-50 text-emerald-700 px-2 py-1 rounded text-xs hover:bg-emerald-100 transition-colors border border-emerald-200 flex items-center gap-1">
                            <FileText className="h-3 w-3" /> {r.number}
                        </Link>
                    ))}
                    {linkedDispatches.map(d => (
                        <Link key={`d-${d.id}`} to={`/dispatch/${d.id}`} target="_blank" className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded text-xs hover:bg-indigo-100 transition-colors border border-indigo-200 flex items-center gap-1">
                            <Send className="h-3 w-3" /> {d.number}
                        </Link>
                    ))}
                    {linkedReceipts.length === 0 && linkedDispatches.length === 0 && (
                        <span className="text-xs text-gray-400">None</span>
                    )}
                </div>
            </div>
        </div>

        {/* Notes List */}
        <div className="flex-1 overflow-y-auto p-6 space-y-0 bg-gray-50">
            <div className="max-w-3xl mx-auto relative">
                 {/* Continuous Vertical Line */}
                 <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

                 {/* Start Marker */}
                 <div className="relative z-10 flex justify-center mb-8">
                    <span className="text-xs font-medium bg-gray-200 text-gray-600 px-3 py-1 rounded-full border border-gray-300">
                        Start of e-File History
                    </span>
                 </div>

                {notes.map((note, index, notesArray) => (
                    <div key={note.id || index} className="relative pl-14 pb-8 group">
                        {/* Timeline Dot */}
                        <div className={`absolute left-6 -translate-x-1/2 top-4 h-4 w-4 rounded-full border-2 bg-white z-10 shadow-sm transition-colors duration-200 ${
                            note.isDraft 
                                ? 'border-yellow-400 ring-2 ring-yellow-50' 
                                : 'border-indigo-600 ring-2 ring-indigo-50 group-hover:ring-indigo-100'
                        }`}></div>
                        
                        {/* Note Card */}
                        <div className={`rounded-xl border shadow-sm transition-all duration-200 ${
                            note.isDraft 
                                ? 'bg-yellow-50 border-yellow-200 shadow-yellow-100' 
                                : 'bg-white border-gray-200 hover:shadow-md hover:border-indigo-200'
                        }`}>
                             {/* Header Info */}
                             <div className={`px-4 py-3 border-b flex justify-between items-center rounded-t-xl ${
                                 note.isDraft ? 'border-yellow-100 bg-yellow-100/50' : 'border-gray-100 bg-gray-50/50'
                             }`}>
                                 <div className="flex items-center gap-3">
                                     <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                         note.isDraft ? 'bg-yellow-200 text-yellow-700' : 'bg-indigo-100 text-indigo-700'
                                     }`}>
                                         {(note.addedByName || 'U').charAt(0)}
                                     </div>
                                     <div className="flex flex-col">
                                         <span className="font-bold text-gray-900 text-sm">{note.addedByName}</span>
                                         <span className="text-[10px] text-gray-500 font-medium uppercase tracking-wide">{note.designationSnapshot || note.addedByRole}</span>
                                     </div>
                                 </div>
                                 <div className="text-xs text-gray-400 font-mono flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {formatDateTime(note.createdAt)}
                                 </div>
                             </div>

                                 {/* Note Content */}
                             <div className="p-4 text-sm text-gray-800 leading-relaxed font-sans">
                                  {(note.subject || file.subject) && (
                                    <div className="mb-3 font-bold text-gray-900 text-sm uppercase underline underline-offset-2">
                                       Subject: {note.subject || file.subject}
                                    </div>
                                 )}
                                  
                                  {note.isDraft === 1 ? (() => {
                                    const isCreator = String(note.addedBy) === String(user?.id);
                                    const canEditDraft = isOnMyDesk && isCreator;
                                    return (
                                    <div 
                                        className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-yellow-300 rounded-lg bg-yellow-50/50 text-yellow-700 gap-2"
                                        title={canEditDraft ? "Click the button below to load content" : "Draft Content Hidden"}
                                    >
                                        <Lock className="h-5 w-5 text-yellow-600" />
                                        <p className="font-medium text-sm">
                                            {canEditDraft ? "Draft Content" : "Draft Content Hidden"}
                                        </p>
                                        <p className="text-xs opacity-80">
                                            {canEditDraft ? "Click the button to load content into editor" : "Resume editing to view content"}
                                        </p>
                                        
                                        {/* Show reason if user is creator but cannot edit */}
                                        {!isOnMyDesk && isCreator && (
                                            <p className="text-xs text-red-500 font-medium bg-red-50 px-2 py-1 rounded border border-red-100">
                                                File must be on your desk to edit
                                            </p>
                                        )}

                                        {/* Visible button for clarity */}
                                        {canEditDraft && (
                                            <Button 
                                                size="sm" 
                                                variant="outline" 
                                                className="mt-2 bg-white text-yellow-700 border-yellow-300 hover:bg-yellow-50 px-4 py-2 h-auto shadow-sm gap-2"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    initiateAction('resume_edit', note.id);
                                                }}
                                                title="Load into Editor"
                                            >
                                                <Edit2 className="h-4 w-4" />
                                                <span>Load into Editor</span>
                                            </Button>
                                        )}
                                    </div>
                                    );
                                 })() : (
                                     <div className="prose prose-sm max-w-none text-justify [&>p]:mb-2 [&>ol]:list-decimal [&>ul]:list-disc [&>ol]:pl-5 [&>ul]:pl-5" dangerouslySetInnerHTML={{ __html: note.remark }} />
                                  )}
                                  
                                  {/* Edit/Commit Draft Button */}
                                  {note.isDraft === 1 && isOnMyDesk && String(note.addedBy) === String(user?.id) && (
                                     <div className="mt-4 pt-3 border-t border-yellow-200 flex justify-end gap-2">
                                         <Button 
                                             size="sm" 
                                             variant="outline"
                                             className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 h-7 text-xs px-2"
                                             onClick={() => handleDeleteDraft(note.id)}
                                         >
                                             <Trash2 className="h-3 w-3 mr-1.5" />
                                             Discard
                                         </Button>
                                         <Button 
                                             size="sm" 
                                             className="bg-yellow-600 hover:bg-yellow-700 text-white border-none h-7 text-xs px-3 shadow-sm"
                                              onClick={() => initiateAction('resume_edit', note.id)}
                                         >
                                             <Edit2 className="h-3 w-3 mr-1.5" />
                                             Edit Draft
                                         </Button>
                                     </div>
                                  )}

                                  {/* Allow editing the LAST finalized note if it is on desk and created by user */}
                                  {isOnMyDesk && !file.isArchived && String(note.addedBy) === String(user?.id) && index === notesArray.length - 1 && note.isDraft !== 1 && (
                                    <div className="mt-4 pt-3 border-t border-gray-100 flex justify-end">
                                        <Button 
                                            size="sm" 
                                            variant="outline"
                                            className="text-indigo-600 border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 h-7 text-xs px-3 shadow-sm"
                                            onClick={() => copyNoteToEditor(note.id)}
                                            title="Copy this note to editor"
                                        >
                                            <Edit2 className="h-3 w-3 mr-1.5" />
                                            Copy to Editor
                                        </Button>
                                    </div>
                                  )}
                                  
                                  {/* Signature Hash Display */}
                                  {note.signatureHash && (
                                     <div className="mt-4 pt-2 border-t border-gray-100 text-[10px] font-mono text-gray-400 flex items-center gap-1 justify-end">
                                         <ShieldCheck className="h-3 w-3 text-green-500" /> 
                                         <span className="text-green-600 font-medium">Signed</span>
                                         <span className="mx-1">•</span>
                                         <span>SHA-256: {note.signatureHash.substring(0, 12)}...</span>
                                     </div>
                                  )}
                             </div>
                        </div>
                    </div>
                 ))}
                 <div ref={notesEndRef} className="h-4" />
            </div>
        </div>

        {/* Input Area */}
        {isOnMyDesk && (
            <div className={`bg-white border-t border-gray-200 shadow-[0_-4px_25px_-5px_rgba(0,0,0,0.1)] z-20 shrink-0 ${isEditorVisible ? (isEditorMaximized ? 'fixed inset-0 z-40 h-screen w-screen m-0 rounded-none' : 'p-6') : 'p-4'}`}>
                {!isEditorVisible ? (
                    <div className="max-w-4xl mx-auto flex items-center justify-end">
                        <Button 
                            onClick={isInitiatorStage ? () => initiateAction('forward') : startNewNoting} 
                            disabled={submitting || forwardLocked || forwardCompleted || initiatorAlreadyForwarded}
                            className="h-9 px-4 bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm shadow-indigo-200 text-xs sm:text-sm"
                        >
                            {isInitiatorStage ? 'Forward' : 'Create Noting'}
                        </Button>
                    </div>
                ) : (
                    <div className={`${isEditorMaximized ? 'h-full flex flex-col' : 'max-w-4xl mx-auto flex flex-col gap-3'}`}>
                        {!isEditorMaximized && (
                             <Input 
                                placeholder="Subject (Optional)" 
                                value={subject} 
                                onChange={e => setSubject(e.target.value)} 
                                className="h-10 text-sm font-medium border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                            />
                        )}
                        
                        <div 
                            id="official-note-editor" 
                            className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col transition-all duration-200 ${
                                isEditorMaximized 
                                    ? 'flex-1 rounded-none border-0' 
                                    : 'h-[350px] ring-1 ring-gray-100'
                            }`}
                        >
                            <div className="bg-white border-b border-gray-100 px-4 py-2 flex items-center justify-between shrink-0">
                                <div className="flex items-center gap-3 text-xs font-medium text-gray-500">
                                    <span className="uppercase tracking-wider">New Note</span>
                                    <div className="h-3 w-px bg-gray-200"></div>
                                    {draftStatus === 'saved' && (newNoteText.trim() || lastSavedAt) && (
                                        <span className="text-green-600 flex items-center gap-1.5 animate-in fade-in duration-300">
                                            <CheckCircle className="w-3.5 h-3.5" /> 
                                            <span>Saved</span>
                                        </span>
                                    )}
                                    {draftStatus === 'saving' && (
                                        <span className="text-indigo-600 flex items-center gap-1.5 animate-in fade-in duration-300">
                                            <div className="w-3 h-3 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div> 
                                            <span>Saving...</span>
                                        </span>
                                    )}
                                    {draftStatus === 'unsaved' && (
                                        <span className="text-amber-600 flex items-center gap-1.5 animate-in fade-in duration-300">
                                            <div className="w-2 h-2 rounded-full bg-amber-500"></div> 
                                            <span>Unsaved</span>
                                        </span>
                                    )}
                                </div>
                                
                                <div className="flex items-center gap-3">
                                    <button 
                                        onClick={() => setIsEditorMaximized(!isEditorMaximized)}
                                        className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                                        title={isEditorMaximized ? "Exit Focus Mode" : "Enter Focus Mode"}
                                    >
                                        {isEditorMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                                    </button>
                                </div>
                            </div>

                            {isEditorMaximized && (
                                <div className="px-6 py-4 bg-white border-b border-gray-100 flex items-center gap-4 animate-in fade-in slide-in-from-top-2 duration-200">
                                    <span className="text-sm font-bold text-gray-500 uppercase tracking-wide">Subject:</span>
                                    <input 
                                        type="text" 
                                        value={subject} 
                                        onChange={e => setSubject(e.target.value)}
                                        placeholder="Enter subject for this note (Optional)..."
                                        className="flex-1 text-lg font-medium text-gray-900 placeholder-gray-300 border-none focus:ring-0 focus:outline-none bg-transparent"
                                        autoFocus
                                    />
                                </div>
                            )}

                            <div id="official-note-editor" className="flex-1 flex flex-col overflow-hidden">
                                <RichTextEditor
                                    value={newNoteText}
                                    onChange={handleNoteChange}
                                    placeholder="Type your official note here..."
                                    className="flex-1 flex flex-col overflow-hidden"
                                    contentClassName="text-base font-sans leading-relaxed"
                                />
                            </div>

                            <div className="bg-white border-t border-gray-100 p-3 flex items-center justify-between gap-4 shrink-0">
                                <div className="text-xs text-gray-400 hidden sm:block pl-2">
                                    {wordCount} words
                                </div>
                                <div className="flex items-center gap-3 ml-auto">
                                    {canAttachInRemarks && (
                                      <>
                                        <input
                                          type="file"
                                          multiple
                                          onChange={handleFileUpload}
                                          className="hidden"
                                          id="file-upload-remark"
                                        />
                                        <Button
                                          onClick={() => document.getElementById('file-upload-remark')?.click()}
                                          variant="ghost"
                                          className="h-8 px-3 text-gray-500 hover:text-gray-700 hover:bg-gray-50 text-xs"
                                        >
                                          <Paperclip className="h-3.5 w-3.5 mr-1.5" />
                                          Add Attachment
                                        </Button>
                                      </>
                                    )}
                                    {(newNoteText || currentDraftId) && (
                                        <Button 
                                            onClick={handleCancelEdit}
                                            variant="ghost" 
                                            className="h-8 px-3 text-red-500 hover:text-red-700 hover:bg-red-50 text-xs"
                                            title="Discard Changes"
                                        >
                                            <XCircle className="h-3.5 w-3.5 mr-1.5" /> 
                                            Cancel
                                        </Button>
                                    )}
                                    <Button 
                                        onClick={() => handleSaveDraft()} 
                                        disabled={submitting || draftStatus === 'saved'} 
                                        variant="ghost" 
                                        className="h-8 px-3 text-gray-500 hover:text-gray-700 hover:bg-gray-50 text-xs"
                                    >
                                        <Save className="h-3.5 w-3.5 mr-1.5" /> 
                                        {draftStatus === 'saving' ? 'Saving...' : 'Save Draft'}
                                    </Button>
                                    
                                    <Button 
                                        onClick={() => initiateAction('forward')} 
                                        disabled={submitting || forwardLocked || forwardCompleted || initiatorAlreadyForwarded} 
                                        className="h-9 px-4 bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm shadow-indigo-200 text-xs sm:text-sm"
                                    >
                                        <Send className="h-4 w-4 mr-2" /> Forward
                                    </Button>
                                    
                                    {canApprove && (
                                        <Button 
                                            onClick={() => initiateAction('approve')} 
                                            disabled={submitting} 
                                            className="h-9 px-4 bg-green-600 hover:bg-green-700 text-white shadow-sm shadow-green-200 text-xs sm:text-sm"
                                        >
                                            <CheckCircle className="h-4 w-4 mr-2" /> Approve
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )}
      </Panel>
      </PanelGroup>

      {isOnMyDesk && !file.isArchived && !isInitiatorStage && (
        <button
          type="button"
          onClick={toggleEditorSection}
          className="fixed bottom-6 right-6 z-50 h-12 px-4 rounded-full shadow-lg bg-indigo-600 hover:bg-indigo-700 text-white flex items-center gap-2 text-sm font-medium"
          title={isEditorVisible ? "Hide Editor" : "Show Editor"}
        >
          {isEditorVisible ? <X className="h-4 w-4" /> : <Edit2 className="h-4 w-4" />}
          {isEditorVisible ? 'Hide Editor' : 'Show Editor'}
        </button>
      )}

      {/* Modals */}
      <ForwardModal 
        isOpen={showForwardModal}
        onClose={() => setShowForwardModal(false)}
        onConfirm={handleForwardConfirm}
        defaultWingId={file.wingId}
        defaultDepartmentId={file.departmentId}
        defaultSectionId={file.sectionId}
        hierarchyType={user?.hierarchyType || 'STANDARD'}
        allowedSignedAs={isInitiatorStage ? ['Initiator'] : ['Forwarder']}
      />

      <Modal isOpen={showPinModal} onClose={() => setShowPinModal(false)} title="Security Check">
        <form onSubmit={(e) => { e.preventDefault(); handlePinSubmit(); }} className="space-y-4">
            <p className="text-gray-600 text-sm">Please enter your e-Sign PIN and MFA code to confirm this action.</p>
            {actionType !== 'resume_edit' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sign As</label>
                <select
                  value={signedAs}
                  onChange={(e) => setSignedAs(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {actionType === 'approve' && <option value="Approver">Approver</option>}
                  {actionType === 'commit' && <option value="Forwarder">Forwarder</option>}
                </select>
              </div>
            )}
            <Input 
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                maxLength={4}
                placeholder="Enter PIN"
                autoComplete="off"
                className="text-center text-2xl tracking-widest font-mono"
            />
            {actionType !== 'resume_edit' && (
              <Input
                type="text"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                maxLength={6}
                placeholder="Enter 6-digit MFA code"
                autoComplete="one-time-code"
                inputMode="numeric"
                className="text-center text-xl tracking-widest font-mono"
              />
            )}
            <div className="flex justify-end gap-2 mt-4">
                <Button variant="secondary" onClick={() => setShowPinModal(false)} type="button">Cancel</Button>
                <Button type="submit" disabled={submitting}>Confirm & Sign</Button>
            </div>
        </form>
      </Modal>

      <Modal
        isOpen={showGreenSheetPreview}
        onClose={() => setShowGreenSheetPreview(false)}
        title="Green Sheet Preview"
      >
        <div className="w-[80vw] max-w-5xl">
          <div className="h-[75vh] border border-gray-200 rounded overflow-hidden bg-white">
            {greenSheetUrl ? (
              <iframe src={greenSheetUrl} className="w-full h-full border-0" title="Green Sheet Preview" />
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500 text-sm">
                Could not load preview
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={handlePrintClick} type="button">
              <Printer className="h-4 w-4 mr-2" /> Download
            </Button>
            <Button variant="secondary" onClick={() => setShowGreenSheetPreview(false)} type="button">Close</Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showLinkModal} onClose={() => setShowLinkModal(false)} title="Link File">
          <div className="space-y-4">
              <div className="flex gap-2">
                  <Input 
                    placeholder="Search by file number or subject..." 
                    value={linkSearchQuery}
                    onChange={(e) => setLinkSearchQuery(e.target.value)}
                  />
                  <Button onClick={handleSearchForLink}>Search</Button>
              </div>
              <div className="max-h-60 overflow-y-auto space-y-2">
                  {linkSearchResults.map(f => (
                      <div key={f.id} className="p-3 border rounded flex justify-between items-center hover:bg-gray-50">
                          <div>
                              <div className="font-bold text-sm">{f.fileNumber}</div>
                              <div className="text-xs text-gray-500">{f.subject}</div>
                          </div>
                          <Button size="sm" onClick={() => handleLinkFile(f.id)}>Link</Button>
                      </div>
                  ))}
                  {linkSearchResults.length === 0 && linkSearchQuery && (
                      <div className="text-center text-gray-500 text-sm">No files found</div>
                  )}
              </div>
          </div>
      </Modal>

      <VersionHistoryModal 
        isOpen={showVersionHistoryModal}
        onClose={() => setShowVersionHistoryModal(false)}
        history={versionHistory}
        onView={handleViewVersion}
      />

      <ConfirmDialog 
        isOpen={showDeleteConfirm} 
        onClose={() => setShowDeleteConfirm(false)} 
        onConfirm={confirmDeleteDraft}
        title="Delete Draft"
        message="Are you sure you want to discard this draft? This action cannot be undone."
        confirmText="Discard Draft"
        variant="danger"
        isLoading={submitting}
      />

      <Modal
        isOpen={showFlagModal}
        onClose={() => setShowFlagModal(false)}
        title="Add Flag"
      >
        <div className="space-y-4">
          <Input
            label="Flag Label"
            value={flagLabel}
            onChange={(e) => setFlagLabel(e.target.value)}
            placeholder="e.g. Flag A"
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" onClick={() => setShowFlagModal(false)}>Cancel</Button>
            <Button onClick={confirmAddFlag}>Add Flag</Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showCreateFlagModal}
        onClose={() => setShowCreateFlagModal(false)}
        title="Create Flag"
      >
        <div className="space-y-4">
          <Input
            label="Flag Label"
            value={flagLabel}
            onChange={(e) => setFlagLabel(e.target.value)}
            placeholder="e.g. Flag A"
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="ghost"
              onClick={() => {
                setShowCreateFlagModal(false);
                setFlagLabel('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={confirmCreateFlag} disabled={!flagLabel.trim()}>
              Create
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showAttachToFlagModal}
        onClose={() => setShowAttachToFlagModal(false)}
        title="Attach To Flag"
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            Selected attachments: <span className="font-bold">{selectedAttachmentRefs.length}</span>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">Select Flag</div>
            <select
              value={attachTargetFlagId}
              onChange={(e) => setAttachTargetFlagId(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white"
            >
              <option value="">Select...</option>
              {flags.map(f => (
                <option key={f.id} value={String(f.id)}>
                  {f.flagLabel}
                </option>
              ))}
            </select>
            {flags.length === 0 && (
              <div className="mt-2 text-xs text-gray-500">
                No flags yet. Create a flag first.
              </div>
            )}
          </div>
          <div className="flex justify-between gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowAttachToFlagModal(false);
                openCreateFlag();
              }}
            >
              Create Flag
            </Button>
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => setShowAttachToFlagModal(false)}>
                Cancel
              </Button>
              <Button onClick={confirmAttachToFlag} disabled={!attachTargetFlagId || selectedAttachmentRefs.length === 0}>
                Attach
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmDialog 
        isOpen={showFileDeleteConfirm} 
        onClose={() => setShowFileDeleteConfirm(false)} 
        onConfirm={confirmDeleteFile}
        title="Delete File"
        message="Are you sure you want to permanently delete this file? This action cannot be undone."
        confirmText="Delete File"
        variant="danger"
        isLoading={submitting}
      />

      <ConfirmDialog 
        isOpen={showArchiveConfirm} 
        onClose={() => setShowArchiveConfirm(false)} 
        onConfirm={confirmArchive}
        title="Archive File"
        message="Are you sure you want to archive this file? It will become read-only and removed from your desk."
        confirmText="Archive File"
        variant="primary"
        isLoading={loading}
      />
    </div>
  );
};

export default FileWorkspace;
